import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  EnvelopeIcon,
  LockClosedIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  HeartIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set, push, update, remove } from 'firebase/database';
import Swal from 'sweetalert2';
import logo from '../../assets/images/logo.jpeg';

// ─── SWEETALERT Z-INDEX ──────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('swal-login-z')) {
  const s = document.createElement('style');
  s.id = 'swal-login-z';
  s.textContent = `.swal2-container{z-index:999999!important}.swal2-on-top{z-index:999999!important}`;
  document.head.appendChild(s);
}

// ─── LOCAL STORAGE HELPERS ───────────────────────────────────────────────────
const LS = {
  get:    (key, fb = null) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  set:    (key, val)       => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} },
  remove: (key)            => { try { localStorage.removeItem(key); } catch {} },
};

// ═══════════════════════════════════════════════════════════════════════════════
// POST-LOGIN MERGE UTILITY
// Merges all guest localStorage data into Firebase after successful login.
// Called once right after login or Google sign-in succeeds.
//
// What it merges:
//   shop_cart        → users/{uid}/cart          (qty-additive merge)
//   liked_products   → users/{uid}/likedProducts  (deduplicate union)
//   wishlist         → users/{uid}/wishlist        (write new entries, skip existing)
//   pending_likes    → productEngagement/{id}/likes (flush engagement records + update totalLikes)
//   user_ratings     → users/{uid}/productRatings  (local wins for any product not yet rated in FB)
//                    → products/{id} averageRating/totalRatings (recalculate)
//   local_whatsapp_orders → already in Firebase (push was done at click time), just clear LS
//   local_views      → products/{id}/totalViews   (add local view counts to Firebase)
// ═══════════════════════════════════════════════════════════════════════════════
export const mergeLocalDataToFirebase = async (uid, userDataInfo = {}) => {
  const log = (msg) => console.log(`[merge] ${msg}`);

  try {
    // ── 1. CART ─────────────────────────────────────────────────────────────
    const lcCart = LS.get('shop_cart', []);
    if (lcCart.length > 0) {
      log(`Merging ${lcCart.length} cart items`);
      const cartRef  = ref(database, `users/${uid}/cart`);
      const cartSnap = await get(cartRef);
      const fbCart   = cartSnap.exists()
        ? (Array.isArray(cartSnap.val()) ? cartSnap.val() : Object.values(cartSnap.val()))
        : [];

      // Qty-additive: if same product exists in FB cart, add local qty on top
      const merged = [...fbCart];
      lcCart.forEach(localItem => {
        const idx = merged.findIndex(i => i.id === localItem.id);
        if (idx >= 0) {
          merged[idx].quantity = (merged[idx].quantity || 1) + (localItem.quantity || 1);
        } else {
          merged.push(localItem);
        }
      });

      await set(cartRef, merged);
      LS.remove('shop_cart');
      log(`Cart merged: ${merged.length} items total`);
    }

    // ── 2. LIKED PRODUCTS (ID array) ────────────────────────────────────────
    const lcLikes = LS.get('liked_products', []);
    let mergedLikeIds = [];
    {
      const likesRef  = ref(database, `users/${uid}/likedProducts`);
      const likesSnap = await get(likesRef);
      const fbLikes   = likesSnap.exists()
        ? (Array.isArray(likesSnap.val()) ? likesSnap.val() : Object.values(likesSnap.val()))
        : [];

      mergedLikeIds = [...new Set([...fbLikes, ...lcLikes])];
      await set(likesRef, mergedLikeIds);

      if (lcLikes.length > 0) {
        LS.remove('liked_products');
        log(`Liked products merged: ${mergedLikeIds.length} total`);
      }
    }

    // ── 3. WISHLIST (rich map) ───────────────────────────────────────────────
    const lcWishlist = LS.get('wishlist', {});
    if (Object.keys(lcWishlist).length > 0) {
      log(`Merging ${Object.keys(lcWishlist).length} wishlist items`);
      for (const [productId, entry] of Object.entries(lcWishlist)) {
        try {
          // Only write if the product is still liked (guards against an
          // unlike-then-login race) AND the entry has isWishlist: true
          if (entry.isWishlist && mergedLikeIds.includes(productId)) {
            const wishRef  = ref(database, `users/${uid}/wishlist/${productId}`);
            const wishSnap = await get(wishRef);
            if (!wishSnap.exists()) {
              // Don't overwrite an existing Firebase wishlist entry
              await set(wishRef, {
                isWishlist:  true,
                productId,
                productName: entry.productName || '',
                categoryId:  entry.categoryId  || null,
                image:       entry.image        || null,
                addedAt:     entry.addedAt      || Date.now(),
              });
            }
          }
        } catch (e) { console.error(`Wishlist merge error for ${productId}:`, e); }
      }
      LS.remove('wishlist');
    }

    // ── 4. PENDING LIKE ENGAGEMENTS ─────────────────────────────────────────
    // These are engagement records that were queued while the user was a guest.
    // Each entry: { product: { id, name, categoryId, ... }, liked: boolean }
    const pendingLikes = LS.get('pending_likes', []);
    if (pendingLikes.length > 0) {
      log(`Flushing ${pendingLikes.length} pending like engagement records`);
      for (const pl of pendingLikes) {
        try {
          const { product, liked: nowLiked } = pl;
          if (!product?.id) continue;

          // Update totalLikes counter on the product
          const productRef  = ref(database, `products/${product.id}`);
          const productSnap = await get(productRef);
          if (productSnap.exists()) {
            const current   = productSnap.val().totalLikes || 0;
            const newCount  = nowLiked ? current + 1 : Math.max(0, current - 1);
            await update(productRef, { totalLikes: newCount });
          }

          // Write / remove the structured engagement record
          const engRef = ref(database, `productEngagement/${product.id}/likes/${uid}`);
          if (nowLiked) {
            await set(engRef, {
              liked:       true,
              isWishlist:  true,
              userId:      uid,
              userName:    userDataInfo?.name  || 'User',
              categoryId:  product.categoryId || null,
              productName: product.name       || '',
              date:        Date.now(),
            });
          } else {
            await remove(engRef);
          }
        } catch (e) { console.error('Pending like flush error:', e); }
      }
      LS.remove('pending_likes');
    }

    // ── 5. PRODUCT RATINGS ───────────────────────────────────────────────────
    const lcRatings = LS.get('user_ratings', {});
    if (Object.keys(lcRatings).length > 0) {
      log(`Merging ${Object.keys(lcRatings).length} product ratings`);
      const ratingsRef  = ref(database, `users/${uid}/productRatings`);
      const ratingsSnap = await get(ratingsRef);
      const fbRatings   = ratingsSnap.exists() ? ratingsSnap.val() : {};

      // Local rating wins for any product not yet rated in Firebase
      // For products that ARE in Firebase already, keep Firebase (user may have
      // updated their rating while logged in elsewhere)
      const mergedRatings = { ...lcRatings, ...fbRatings }; // FB wins on conflict
      await set(ratingsRef, mergedRatings);

      // Back-propagate any truly new local ratings onto the product aggregate
      for (const [productId, ratingData] of Object.entries(lcRatings)) {
        if (fbRatings[productId]) continue; // already counted in Firebase
        try {
          const productRef  = ref(database, `products/${productId}`);
          const productSnap = await get(productRef);
          if (!productSnap.exists()) continue;
          const prod    = productSnap.val();
          let total     = prod.totalRatings  || 0;
          let avg       = prod.averageRating || 0;
          total++;
          avg = ((avg * (total - 1)) + ratingData.rating) / total;
          await update(productRef, { averageRating: avg, totalRatings: total });
        } catch (e) { console.error(`Rating propagation error for ${productId}:`, e); }
      }
      LS.remove('user_ratings');
    }

    // ── 6. LOCAL PRODUCT VIEWS ───────────────────────────────────────────────
    // Guest view counts accumulated locally; flush to Firebase totalViews
    const lcViews = LS.get('local_views', {});
    if (Object.keys(lcViews).length > 0) {
      log(`Flushing view counts for ${Object.keys(lcViews).length} products`);
      for (const [productId, viewCount] of Object.entries(lcViews)) {
        try {
          const productRef  = ref(database, `products/${productId}`);
          const productSnap = await get(productRef);
          if (productSnap.exists()) {
            await update(productRef, {
              totalViews: (productSnap.val().totalViews || 0) + viewCount,
            });
          }
        } catch (e) { console.error(`View flush error for ${productId}:`, e); }
      }
      LS.remove('local_views');
    }

    // ── 7. WHATSAPP ORDERS ───────────────────────────────────────────────────
    // These were already pushed to Firebase at click-time (even for guests) so
    // we just need to link them to the now-known uid and clear local copy.
    const lcOrders = LS.get('local_whatsapp_orders', []);
    if (lcOrders.length > 0) {
      log(`Linking ${lcOrders.length} guest WhatsApp orders to uid ${uid}`);
      for (const order of lcOrders) {
        try {
          if (order.id) {
            // Update the existing Firebase record with the now-known userId
            await update(ref(database, `whatsappOrders/${order.id}`), {
              userId:    uid,
              userEmail: userDataInfo?.email || null,
              userName:  userDataInfo?.name  || 'User',
              linkedAt:  Date.now(),
            });
          }
        } catch (e) { console.error(`WA order link error for ${order.id}:`, e); }
      }
      LS.remove('local_whatsapp_orders');
    }

    log('All local data merged successfully ✓');
    return { success: true };
  } catch (e) {
    console.error('[merge] Fatal error during merge:', e);
    return { success: false, error: e.message };
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const Login = () => {
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [showPassword,setShowPassword]= useState(false);
  const [loading,     setLoading]     = useState(false);
  const [merging,     setMerging]     = useState(false); // shows while syncing local data
  const [isFocused,   setIsFocused]   = useState({ email: false, password: false });

  const { login, googleSignIn, resetPassword, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();

  // ─── ALERTS ────────────────────────────────────────────────────────────────
  const showSuccessAlert = (message) =>
    Swal.fire({
      position: 'top-end', icon: 'success', title: message,
      showConfirmButton: false, timer: 2000, backdrop: 'transparent', toast: true,
      customClass: { popup: 'rounded-2xl shadow-2xl border-l-4 border-green-500', title: 'text-gray-800 text-sm font-medium' },
    });

  const showErrorAlert = (message) =>
    Swal.fire({
      position: 'top-end', icon: 'error', title: message,
      showConfirmButton: false, timer: 3000, backdrop: 'transparent', toast: true,
      customClass: { popup: 'rounded-2xl shadow-2xl border-l-4 border-red-500', title: 'text-gray-800 text-sm font-medium' },
    });

  const showVerificationDialog = () =>
    Swal.fire({
      title: 'Email Verification',
      html: `
        <div class="py-4">
          <div class="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <p class="text-gray-600 mb-2">Your email needs verification</p>
          <p class="text-sm text-gray-500 mb-4">We'll send a new verification link to:</p>
          <p class="font-semibold text-primary text-lg mb-6">${email}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Resend Link',
      cancelButtonText: 'Close',
      confirmButtonColor: '#800020',
      cancelButtonColor: '#6b7280',
      background: '#ffffff',
      customClass: {
        popup: 'rounded-3xl shadow-2xl p-6',
        title: 'text-2xl font-bold text-gray-900 mb-2',
        confirmButton: 'px-6 py-3 rounded-xl font-semibold transition-all duration-300',
        cancelButton: 'px-6 py-3 rounded-xl font-semibold transition-all duration-300',
      },
      preConfirm: async () => {
        const result = await resendVerificationEmail();
        if (result.success) {
          Swal.fire({ icon: 'success', title: 'Verification Sent!', text: 'Check your inbox.', timer: 2000, showConfirmButton: false, customClass: { popup: 'rounded-2xl' } });
        } else {
          showErrorAlert(result.error);
        }
      },
    });

  const showForgotPasswordDialog = () =>
    Swal.fire({
      title: 'Reset Password',
      html: `
        <div class="py-4">
          <div class="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p class="text-gray-600 mb-4">Enter your email and we'll send you a reset link</p>
          <input type="email" id="reset-email" class="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-0 transition-all duration-300" placeholder="your@email.com" style="height:45px;">
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Send Reset Link',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#800020',
      cancelButtonColor: '#6b7280',
      background: '#ffffff',
      customClass: {
        popup: 'rounded-3xl shadow-2xl',
        title: 'text-2xl font-bold text-gray-900',
        confirmButton: 'px-6 py-2 rounded-xl font-semibold transition-all duration-300',
        cancelButton: 'px-6 py-2 rounded-xl font-semibold transition-all duration-300',
      },
      preConfirm: async () => {
        const resetEmail = document.getElementById('reset-email').value;
        if (!resetEmail) { Swal.showValidationMessage('Email is required'); return false; }
        const result = await resetPassword(resetEmail);
        if (result.success) {
          Swal.fire({ icon: 'success', title: 'Email Sent!', text: 'Check your inbox.', timer: 2000, showConfirmButton: false, customClass: { popup: 'rounded-2xl' } });
        } else {
          showErrorAlert(result.error);
        }
      },
    });

  // ─── AFTER-LOGIN MERGE HANDLER ─────────────────────────────────────────────
  // Checks if there's any local data worth merging before redirecting
  const handlePostLoginMerge = async (uid, userInfo) => {
    const hasLocalCart     = (LS.get('shop_cart', []).length > 0);
    const hasLocalLikes    = (LS.get('liked_products', []).length > 0);
    const hasLocalWishlist = (Object.keys(LS.get('wishlist', {})).length > 0);
    const hasPendingLikes  = (LS.get('pending_likes', []).length > 0);
    const hasLocalRatings  = (Object.keys(LS.get('user_ratings', {})).length > 0);
    const hasLocalViews    = (Object.keys(LS.get('local_views', {})).length > 0);
    const hasLocalOrders   = (LS.get('local_whatsapp_orders', []).length > 0);

    const hasAnyData = hasLocalCart || hasLocalLikes || hasLocalWishlist ||
                       hasPendingLikes || hasLocalRatings || hasLocalViews || hasLocalOrders;

    if (!hasAnyData) return; // nothing to merge, skip silently

    setMerging(true);

    // Show a non-blocking toast so the user knows syncing is happening
    const mergingToast = Swal.fire({
      title: 'Syncing your data…',
      html: `
        <div style="display:flex;align-items:center;gap:10px;padding:4px 0">
          <div style="width:18px;height:18px;border:3px solid #fecaca;border-top-color:#800020;border-radius:50%;animation:spin 0.7s linear infinite;flex-shrink:0"></div>
          <span style="color:#6b7280;font-size:13px">Saving your cart, favourites & activity…</span>
        </div>
        <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      toast: false,
      position: 'center',
      width: 340,
      background: '#fff',
      customClass: { popup: 'rounded-2xl shadow-xl', container: 'swal2-on-top' },
    });

    const result = await mergeLocalDataToFirebase(uid, userInfo);

    // Close the syncing dialog
    Swal.close();
    setMerging(false);

    if (result.success) {
      // Brief success feedback
      await Swal.fire({
        title: '✓ All your data is saved',
        html: `
          <div style="color:#6b7280;font-size:13px;line-height:1.6;padding:4px 0">
            ${hasLocalCart     ? '<span style="display:block">🛒 Cart items merged</span>' : ''}
            ${hasLocalLikes || hasLocalWishlist ? '<span style="display:block">❤️ Favourites & wishlist saved</span>' : ''}
            ${hasLocalRatings  ? '<span style="display:block">⭐ Your ratings recorded</span>' : ''}
            ${hasLocalViews    ? '<span style="display:block">👁 Activity synced</span>' : ''}
            ${hasLocalOrders   ? '<span style="display:block">💬 WhatsApp orders linked</span>' : ''}
          </div>
        `,
        icon: 'success',
        timer: 2200,
        showConfirmButton: false,
        position: 'center',
        width: 320,
        customClass: { popup: 'rounded-2xl shadow-xl', container: 'swal2-on-top' },
      });
    }
    // If merge failed partially we log it but don't block the user — they're
    // already logged in. Shop's loadFromFirebase will re-attempt on next load.
  };

  // ─── EMAIL/PASSWORD SUBMIT ─────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        const uid      = result.user?.uid;
        const userInfo = {
          name:  result.user?.displayName || '',
          email: result.user?.email       || email,
        };

        showSuccessAlert('Welcome back!');

        // Run merge in the background while staying on login page briefly
        if (uid) await handlePostLoginMerge(uid, userInfo);

        navigate('/');
      } else {
        setLoading(false);
        if (result.needsVerification) {
          showVerificationDialog();
        } else {
          showErrorAlert(result.error || 'Invalid credentials');
        }
      }
    } catch (error) {
      setLoading(false);
      showErrorAlert('Login failed. Please try again.');
    }
  };

  // ─── GOOGLE SIGN-IN ────────────────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await googleSignIn();

      if (result.success) {
        const uid      = result.user?.uid;
        const userInfo = {
          name:  result.user?.displayName || '',
          email: result.user?.email       || '',
        };

        showSuccessAlert('Google login successful!');

        if (uid) await handlePostLoginMerge(uid, userInfo);

        navigate('/');
      } else {
        showErrorAlert(result.error || 'Google sign-in failed');
        setLoading(false);
      }
    } catch (error) {
      showErrorAlert(error.message || 'Google sign-in failed. Please try again.');
      setLoading(false);
    }
  };

  const advantages = [
    { icon: '💪', text: 'Modern Equipment' },
    { icon: '👥', text: 'Expert Trainers'  },
    { icon: '🌡️', text: 'Sauna & Steam'    },
    { icon: '🛍️', text: 'Member Discounts' },
    { icon: '⏱️', text: '24/7 Access'      },
    { icon: '🎯', text: 'Free Classes'     },
  ];

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black flex">

      {/* ── LEFT SIDE ─────────────────────────────────────────────────── */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={logo} alt="PowerCore Fitness" className="w-full h-full object-cover rounded-r-3xl" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent rounded-r-3xl" />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-center p-12 text-white">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="flex items-center space-x-3 mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/30 rounded-xl blur-xl" />
                <img src={logo} alt="PowerCore" className="h-12 w-auto relative rounded-lg border-2 border-primary/30" />
              </div>
              <div>
                <h1 className="text-3xl font-black"><span className="text-primary">POWER</span><span className="text-white">CORE</span></h1>
                <p className="text-gray-400 text-xs tracking-wider">FITNESS</p>
              </div>
            </div>
            <h2 className="text-4xl font-bold mb-4 leading-tight">Welcome Back,<br /><span className="text-primary">Champion</span></h2>
            <p className="text-gray-300 text-base mb-6 max-w-lg">Continue your fitness journey. Access workouts, track progress and shop with member prices.</p>
            <div className="grid grid-cols-2 gap-3 mb-8">
              {advantages.map((adv, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * i }}
                  className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-lg p-2 border border-white/20 hover:bg-white/20 transition-all duration-300 group">
                  <span className="text-xl">{adv.icon}</span>
                  <span className="text-xs text-gray-200 group-hover:text-white">{adv.text}</span>
                </motion.div>
              ))}
            </div>
            <div className="border-l-4 border-primary pl-3 italic text-gray-300">
              <p className="text-sm">"The best fitness decision I ever made!"</p>
              <p className="text-xs text-primary mt-1">— Sarah K.</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── RIGHT SIDE — Form ─────────────────────────────────────────── */}
      <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="w-full max-w-md">

          <div className="mb-6 text-center lg:text-left">
            <h2 className="text-3xl font-bold mb-1 text-white">Sign <span className="text-primary">In</span></h2>
            <p className="text-gray-400 text-sm">Access your PowerCore account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Email */}
            <div>
              <label className="text-xs font-medium text-gray-300 ml-1 mb-1 block">Email</label>
              <div className={`relative group transition-all duration-300 ${isFocused.email ? 'scale-[1.01]' : ''}`}>
                <EnvelopeIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-all duration-300 ${isFocused.email ? 'text-primary' : 'text-gray-500'}`} />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  onFocus={() => setIsFocused({ ...isFocused, email: true })}
                  onBlur={() => setIsFocused({ ...isFocused, email: false })}
                  required
                  className="w-full pl-9 pr-3 py-2 bg-gray-900 border-2 border-gray-800 rounded-lg focus:border-primary focus:bg-gray-800 focus:ring-0 transition-all duration-300 outline-none text-white placeholder-gray-600 text-sm"
                  placeholder="your@email.com" style={{ height: '45px' }} />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="text-xs font-medium text-gray-300 ml-1 mb-1 block">Password</label>
              <div className={`relative group transition-all duration-300 ${isFocused.password ? 'scale-[1.01]' : ''}`}>
                <LockClosedIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-all duration-300 ${isFocused.password ? 'text-primary' : 'text-gray-500'}`} />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  onFocus={() => setIsFocused({ ...isFocused, password: true })}
                  onBlur={() => setIsFocused({ ...isFocused, password: false })}
                  required
                  className="w-full pl-9 pr-9 py-2 bg-gray-900 border-2 border-gray-800 rounded-lg focus:border-primary focus:bg-gray-800 focus:ring-0 transition-all duration-300 outline-none text-white placeholder-gray-600 text-sm"
                  placeholder="••••••••" style={{ height: '45px' }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-primary transition-colors">
                  {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Forgot password */}
            <div className="flex justify-end">
              <button type="button" onClick={showForgotPasswordDialog} className="text-xs text-gray-400 hover:text-primary transition-colors">
                Forgot password?
              </button>
            </div>

            {/* Sign In button */}
            <motion.button
              whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
              type="submit" disabled={loading || merging}
              className="w-full bg-gradient-to-r from-primary to-primary-dark text-white py-2 rounded-lg font-semibold shadow-lg shadow-primary/30 hover:shadow-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center space-x-2"
              style={{ height: '45px' }}>
              {(loading || merging) ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <><span className="text-sm">Sign In</span><ArrowRightIcon className="h-4 w-4" /></>
              )}
            </motion.button>

            {/* Google Sign In */}
            <motion.button
              whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
              type="button" onClick={handleGoogleSignIn} disabled={loading || merging}
              className="w-full bg-white text-gray-900 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center justify-center space-x-2 border-2 border-gray-800 disabled:opacity-50"
              style={{ height: '45px' }}>
              {(loading || merging) ? (
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="h-4 w-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-sm">Continue with Google</span>
                </>
              )}
            </motion.button>

            {/* Register link */}
            <div className="text-center pt-2">
              <p className="text-xs text-gray-400">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary font-bold hover:text-primary-dark transition-colors">Create one</Link>
              </p>
            </div>
          </form>

          {/* Trust badges */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="flex justify-center space-x-4 mt-6">
            {[
              { icon: <ShieldCheckIcon className="h-3 w-3 text-primary" />, label: 'Secure'      },
              { icon: <HeartIcon       className="h-3 w-3 text-primary" />, label: '24/7 Support' },
              { icon: <SparklesIcon    className="h-3 w-3 text-primary" />, label: 'Premium'      },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center space-x-1 text-gray-500 text-xs">
                {icon}<span>{label}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;